import { PhoenixTrpcClient, RouterOutput } from 'apps/phoenix/src/app/utils';
import { isEqual } from 'lodash';
import {
  Observable,
  Subject,
  catchError,
  forkJoin,
  from,
  map,
  merge,
  of,
  takeUntil,
} from 'rxjs';

import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

import {
  ChipsAutocomplete,
  ChipsServerSideAutocomplete,
} from '../../interfaces';
import { UiHelpersService } from '../../services';

type ClientItem = RouterOutput['client']['autocomplete'][0];
type ClientStub = Pick<ClientItem, 'id'>;

@Component({
  selector: 'phoenix-client-list-autocomplete',
  template: `
    <phoenix-chips
      [title]="title"
      [ariaLabel]="ariaLabel"
      [control]="decoratedControl"
      [autocompleteConfig]="autocompleteConfig"
      [displayValueFormat]="displayValueFormat"
      [errorMessages]="errorMessages"
      [loadingAutocomplete]="isRefreshing || loading"
      [isLoading]="loading"
      [disabled]="disabled"
    >
      @if (showSelectAll && clientOptions?.length && !disabled) {
        <button
          type="button"
          mat-icon-button
          color="primary"
          class="material-icons-outlined ml-2"
          matTooltip="Select all clients"
          [attr.aria-label]="'Select all ' + title"
          [disabled]="loading || isRefreshing"
          (click)="selectAll()"
        >
          group_add
        </button>
      }
      <ng-content></ng-content
    ></phoenix-chips>
  `,
  standalone: false,
})
export class ClientListAutocompleteComponent
  implements OnInit, OnChanges, OnDestroy
{
  phoenixTrpc = inject(PhoenixTrpcClient);
  uiHelper = inject(UiHelpersService);

  @Input() control = new FormControl<string[]>([], { nonNullable: true });
  @Input() title = 'Clients';
  @Input() ariaLabel = '';
  @Input() refresh = new Observable<void>();
  @Input() portal = false;
  @Input() clientOptions: ClientItem[] | null = null;
  @Input() fetchOnFocus = false;
  @Input() noOptionsMessage = '';
  @Input() errorMessages?: Record<string, string>;
  @Input() loading = false;
  @Input() showSelectAll = false;

  decoratedControl = new FormControl<(ClientItem | ClientStub)[]>([], {
    nonNullable: true,
  });
  disabled = false;
  isRefreshing = false;

  private readonly destroy$ = new Subject<void>();
  private readonly controlDestroy$ = new Subject<void>();

  private readonly stateClasses: Record<string, string> = {
    ACTIVE: 'theme-bg-success text-white',
    PROSPECTIVE: 'theme-bg-primary text-white',
    INTERNAL: 'theme-bg-primary text-white',
    INACTIVE: 'theme-bg-warning theme-text-1',
    DELETED: 'theme-bg-error text-white',
  };

  autocompleteConfig: ChipsAutocomplete | ChipsServerSideAutocomplete =
    this.buildServerSideAutocompleteConfig();

  private buildServerSideAutocompleteConfig(): ChipsServerSideAutocomplete {
    return {
      serverCallback: (val: string) => this.searchClients(val),
      pasteCallback: (pastedValue: string): Observable<ClientItem[]> => {
        const selectedIds = this.selectedIdSet();
        const ids = pastedValue
          .split(/[,\s;]+/)
          .map(id => id.trim())
          .filter(Boolean)
          .filter(id => !selectedIds.has(this.normalizeId(id)));

        if (!ids.length) return of([]);

        return forkJoin(
          ids.map(id =>
            from(
              this.phoenixTrpc.client.autocomplete.query({
                search: id,
                portal: this.portal,
              })
            ).pipe(
              map(results => results[0]),
              catchError(() => of(undefined))
            )
          )
        ).pipe(map(results => results.filter(Boolean) as ClientItem[]));
      },
      optionHtml: (option: ClientItem) => this.optionHtml(option),
    };
  }

  private buildLocalAutocompleteConfig(): ChipsAutocomplete {
    return {
      optionList: this.availableClientOptions(),
      customFilter: option =>
        typeof option === 'string'
          ? option
          : this.displayValueFormat(option as ClientItem),
      pasteCallback: pastedValue => this.resolveLocalPastedClients(pastedValue),
      optionHtml: option => this.optionHtml(option as ClientItem),
      noOptionsMessage: this.noOptionsMessage,
    };
  }

  // Selects all available clients and syncs their IDs to the form control.
  selectAll(): void {
    const options = this.clientOptions ?? [];
    if (this.disabled || this.decoratedControl.disabled || !options.length) {
      return;
    }

    this.decoratedControl.setValue([...options]);
    this.decoratedControl.markAsDirty();
  }

  displayValueFormat = (value: ClientItem | ClientStub): string => {
    if ('fullName' in value && value.fullName) {
      return `${value.fullName} (${value.id})`;
    }
    return value.id ?? '';
  };

  ngOnInit(): void {
    this.refresh.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.isRefreshing) {
        this.refreshClients();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['clientOptions'] ||
      changes['fetchOnFocus'] ||
      changes['noOptionsMessage']
    ) {
      this.refreshAutocompleteConfig();
    }

    if (changes['control']) {
      this.controlDestroy$.next();
      this.isRefreshing = false;
      this.syncDisabledState();

      this.mirrorRequiredValidator();
      this.syncToDecorated();
      this.syncValidationState();

      // Mirrors validation and interaction state to the decorated control.
      this.control.events
        .pipe(takeUntil(this.controlDestroy$))
        .subscribe(() => {
          this.syncValidationState();
        });

      this.control.valueChanges
        .pipe(takeUntil(this.controlDestroy$))
        .subscribe(() => {
          const decoratedIds = (this.decoratedControl.value ?? []).map(
            c => c.id
          );
          if (!isEqual(decoratedIds, this.control.value ?? [])) {
            this.syncToDecorated();
          }
          this.refreshAutocompleteConfig();
        });

      this.decoratedControl.valueChanges
        .pipe(takeUntil(this.controlDestroy$))
        .subscribe(decorated => {
          const ids = (decorated ?? []).map(c => c.id);
          if (!isEqual(ids, this.control.value ?? [])) {
            this.control.setValue(ids as string[], { emitEvent: false });
            this.control.markAsDirty();
            // Restores interaction state after the silent value update.
            this.syncValidationState();
            this.refreshAutocompleteConfig();
          }
        });

      this.control.statusChanges
        .pipe(takeUntil(this.controlDestroy$))
        .subscribe(() => {
          this.syncDisabledState();
        });
    }
  }

  // Mirrors the required validator to the decorated control.
  private mirrorRequiredValidator(): void {
    if (this.control.hasValidator(Validators.required)) {
      this.decoratedControl.addValidators(Validators.required);
    } else {
      this.decoratedControl.removeValidators(Validators.required);
    }
    this.decoratedControl.updateValueAndValidity({ emitEvent: false });
  }

  // Mirrors touched and dirty state to the decorated control.
  private syncValidationState(): void {
    if (this.control.touched && this.decoratedControl.untouched) {
      this.decoratedControl.markAsTouched();
    } else if (this.control.untouched && this.decoratedControl.touched) {
      this.decoratedControl.markAsUntouched();
    }

    if (this.control.dirty && this.decoratedControl.pristine) {
      this.decoratedControl.markAsDirty();
    } else if (this.control.pristine && this.decoratedControl.dirty) {
      this.decoratedControl.markAsPristine();
    }
  }

  // Mirrors the disabled state to the decorated control.
  private syncDisabledState(): void {
    this.disabled = this.control.disabled;

    if (this.control.disabled !== this.decoratedControl.disabled) {
      if (this.control.disabled) {
        this.decoratedControl.disable();
      } else {
        this.decoratedControl.enable();
      }
    }
  }

  private refreshAutocompleteConfig(): void {
    this.autocompleteConfig = this.clientOptions
      ? this.buildLocalAutocompleteConfig()
      : this.buildServerSideAutocompleteConfig();

    if ('serverCallback' in this.autocompleteConfig) {
      this.autocompleteConfig.fetchOnFocus = this.fetchOnFocus;
      this.autocompleteConfig.noOptionsMessage = this.noOptionsMessage;
    }
  }

  private searchClients(value: string): Observable<ClientItem[]> {
    const selectedIds = this.selectedIdSet();

    return from(
      this.phoenixTrpc.client.autocomplete.query({
        search: value,
        portal: this.portal,
      })
    ).pipe(
      map(results =>
        results.filter(client => !selectedIds.has(this.normalizeId(client.id)))
      )
    );
  }

  private selectedIdSet(): Set<string> {
    return new Set((this.control.value ?? []).map(id => this.normalizeId(id)));
  }

  private availableClientOptions(): ClientItem[] {
    const selectedIds = this.selectedIdSet();
    return (this.clientOptions ?? []).filter(
      client => !selectedIds.has(this.normalizeId(client.id))
    );
  }

  private resolveLocalPastedClients(
    pastedValue: string
  ): Observable<ClientItem[]> {
    const selectedIds = this.selectedIdSet();
    const ids = pastedValue
      .split(/[,\s;]+/)
      .map(id => id.trim())
      .filter(Boolean)
      .filter(id => !selectedIds.has(this.normalizeId(id)));

    if (!ids.length) return of([]);

    const optionsById = new Map(
      this.availableClientOptions().map(client => [
        this.normalizeId(client.id),
        client,
      ])
    );

    return of(
      ids
        .map(id => optionsById.get(this.normalizeId(id)))
        .filter(Boolean) as ClientItem[]
    );
  }

  private normalizeId(id: string): string {
    return id.trim().toLowerCase();
  }

  private optionHtml(option: ClientItem): string {
    const stateClass =
      this.stateClasses[option.state ?? ''] ?? 'gray-theme-bg-1 theme-text-1';
    const partner = option.partnerInfo
      ? `<span class="text-sm theme-text-2">${option.partnerInfo}</span>`
      : '';
    const state = option.state
      ? `<span class="text-sm ${stateClass} px-2 rounded ml-auto">${option.state}</span>`
      : '';

    return `
        <div class="flex items-center gap-3">
          <span class="font-bold">${option.fullName}</span>
          <span class="text-sm theme-text-2">(${option.id})</span>
          ${partner}
          ${state}
        </div>`;
  }

  private syncToDecorated(): void {
    const ids = this.control.value ?? [];
    const current = this.decoratedControl.value ?? [];

    const decorated = ids.map(id => current.find(c => c.id === id) ?? { id });

    this.decoratedControl.setValue(decorated, { emitEvent: false });

    if (ids.length && decorated.some(c => !('fullName' in c))) {
      this.isRefreshing = false;
      this.refreshClients();
    }
  }

  refreshClients(): void {
    const ids = this.control.value ?? [];
    if (!ids.length) return;

    this.isRefreshing = true;
    forkJoin(
      ids.map(id =>
        from(
          this.phoenixTrpc.client.autocomplete.query({
            search: id,
            portal: this.portal,
          })
        )
      )
    )
      .pipe(takeUntil(merge(this.controlDestroy$, this.destroy$)))
      .subscribe({
        next: results => {
          const clients: (ClientItem | ClientStub)[] = results.map(
            (items, i) => {
              const id = ids[i];
              return items.find(c => c.id === id) ?? { id };
            }
          );
          this.decoratedControl.setValue(clients, { emitEvent: false });
          this.isRefreshing = false;
        },
        error: error => {
          this.isRefreshing = false;
          this.uiHelper.handleErrorMessage(error, 'Error loading client data');
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.controlDestroy$.next();
    this.controlDestroy$.complete();
  }
}
